import * as React from "react";
import { Link } from "react-router-dom";
import {
  Check,
  X,
  ArrowUpCircle,
  FileCheck,
  Banknote,
  ShieldCheck,
  UserPlus,
  Flag,
  Cog,
  ArrowRight,
} from "lucide-react";
import { cn } from "../cn";
import { formatRelativeShort } from "../format";
import type { AuditEvent } from "@/domain/types";

// One structured activity primitive shared by the right-rail feed and the full audit
// trail, so the two can never drift. Each event is icon-by-kind + a title / detail /
// meta structure, with the amount pulled out of the sentence into its own badge and a
// terse timestamp. The kind drives the icon and its tone together.
export type ActivityKind =
  | "approval"
  | "rejected"
  | "spend"
  | "evidence"
  | "disbursement"
  | "kyc"
  | "onboarding"
  | "status_change"
  | "system";

const KIND_META: Record<ActivityKind, { icon: React.ElementType; bg: string; fg: string }> = {
  approval: { icon: Check, bg: "bg-okSoft", fg: "text-ok" },
  rejected: { icon: X, bg: "bg-badSoft", fg: "text-bad" },
  spend: { icon: ArrowUpCircle, bg: "bg-infoSoft", fg: "text-info" },
  evidence: { icon: FileCheck, bg: "bg-infoSoft", fg: "text-info" },
  disbursement: { icon: Banknote, bg: "bg-okSoft", fg: "text-ok" },
  kyc: { icon: ShieldCheck, bg: "bg-infoSoft", fg: "text-info" },
  onboarding: { icon: UserPlus, bg: "bg-neutralSoft", fg: "text-faint" },
  status_change: { icon: Flag, bg: "bg-warnSoft", fg: "text-warn" },
  system: { icon: Cog, bg: "bg-neutralSoft", fg: "text-faint" },
};

export type ActivityFeedItem = {
  id: string;
  kind: ActivityKind;
  /** Verb-first headline, e.g. "Spend ask approved". */
  title: string;
  /** Secondary line — the object / sentence. */
  detail?: string;
  /** Small trailing meta, e.g. actor org or sub-project. */
  meta?: string;
  /** Pre-formatted money badge (the caller applies the persona currency), e.g. "42M UGX". */
  amount?: string;
  /** ISO timestamp. */
  timestamp: string;
  /** When set, the whole row links here. */
  href?: string;
};

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function dayGroup(iso: string, now: Date): string {
  const days = Math.floor((startOfDay(now) - startOfDay(new Date(iso))) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return "This week";
  if (days < 30) return "This month";
  return "Earlier";
}

type Density = "comfortable" | "compact";

function Row({ item, isLast, density }: { item: ActivityFeedItem; isLast: boolean; density: Density }) {
  const m = KIND_META[item.kind];
  const Icon = m.icon;
  // Compact is the rail's AMBIENT lane: it steps DOWN from the action list so the eye
  // ranks action first. Smaller circle, lighter title — and crucially no per-row
  // affordance, because the absence of affordance is what teaches "this lane is read-only".
  const compact = density === "compact";
  const inner = (
    <>
      <div className="relative flex flex-col items-center">
        <span
          className={cn(
            "z-10 flex shrink-0 items-center justify-center rounded-full",
            compact ? "h-6 w-6" : "h-7 w-7",
            m.bg,
          )}
        >
          <Icon className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5", m.fg)} aria-hidden />
        </span>
        {!isLast && <span className={cn("absolute w-px bg-line", compact ? "top-6 h-[calc(100%-0.75rem)]" : "top-7 h-[calc(100%-1rem)]")} aria-hidden />}
      </div>
      <div className="-mt-0.5 min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className={cn("truncate text-[13px]", compact ? "font-normal text-mute" : "font-medium text-ink")}>{item.title}</span>
          <span className="shrink-0 text-[11px] tabular-nums text-faint">{formatRelativeShort(item.timestamp)}</span>
        </div>
        {item.detail && <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-mute">{item.detail}</p>}
        {(item.amount || item.meta) && (
          <div className="mt-1 flex items-center gap-2 text-[11px] text-faint">
            {item.amount && <span className="rounded bg-subtle px-1.5 py-0.5 font-medium tabular-nums text-mute">{item.amount}</span>}
            {item.meta && <span className="truncate">{item.meta}</span>}
          </div>
        )}
      </div>
    </>
  );
  return (
    <li>
      {item.href ? (
        <Link to={item.href} className="flex gap-3 rounded-md pb-4 transition-colors hover:bg-subtle">
          {inner}
        </Link>
      ) : (
        <div className="flex gap-3 pb-4">{inner}</div>
      )}
    </li>
  );
}

/** Structured activity feed with day grouping, a timeline rail and an optional view-all. */
export function ActivityFeed({
  items,
  groupByDay = true,
  viewAllHref,
  onViewAll,
  emptyLabel = "No activity yet.",
  density = "comfortable",
  className,
}: {
  items: ActivityFeedItem[];
  groupByDay?: boolean;
  viewAllHref?: string;
  onViewAll?: () => void;
  emptyLabel?: string;
  /** "compact" steps the feed down for the rail's ambient lane; the full audit trail stays "comfortable". */
  density?: Density;
  className?: string;
}) {
  if (items.length === 0) {
    return <p className={cn("text-xs text-faint", className)}>{emptyLabel}</p>;
  }
  const now = new Date();
  // Group consecutively; items are expected pre-sorted newest-first.
  const groups: { label: string; items: ActivityFeedItem[] }[] = [];
  for (const it of items) {
    const label = groupByDay ? dayGroup(it.timestamp, now) : "";
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(it);
    else groups.push({ label, items: [it] });
  }
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {groups.map((g, gi) => (
        <div key={gi}>
          {groupByDay && (
            <p
              className={cn(
                "mb-2 text-[11px] text-faint",
                density === "compact"
                  ? "lowercase tracking-normal"
                  : "font-semibold uppercase tracking-wide",
              )}
            >
              {g.label}
            </p>
          )}
          <ol aria-live="polite" className="flex flex-col">
            {g.items.map((it, i) => (
              <Row key={it.id} item={it} density={density} isLast={gi === groups.length - 1 && i === g.items.length - 1} />
            ))}
          </ol>
        </div>
      ))}
      {(viewAllHref || onViewAll) &&
        (viewAllHref ? (
          <Link to={viewAllHref} className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">
            View all activity <ArrowRight className="h-3 w-3" />
          </Link>
        ) : (
          <button type="button" onClick={onViewAll} className="inline-flex items-center gap-1 self-start text-xs font-medium text-brand hover:underline">
            View all activity <ArrowRight className="h-3 w-3" />
          </button>
        ))}
    </div>
  );
}

// Shared mapping from an audit event onto the structured activity vocabulary, so every
// surface (rails + screens) builds feed items the same way. Money is passed in already
// formatted, because the persona currency comes from a hook the caller holds.
export function activityKindOf(e: AuditEvent): ActivityKind {
  if (e.class === "approval") {
    if (/^Rejected/i.test(e.description)) return "rejected";
    if (/disbursement/i.test(e.description)) return "disbursement";
    return "approval";
  }
  return e.class;
}

export function activityTitleOf(e: AuditEvent): string {
  switch (activityKindOf(e)) {
    case "approval": return "Spend ask approved";
    case "rejected": return "Spend ask rejected";
    case "disbursement": return "Disbursement approved";
    case "spend": return "Spend ask raised";
    case "evidence": return /Confirmed/i.test(e.description) ? "Spend confirmed" : "Evidence submitted";
    case "kyc": return "KYC reviewed";
    case "onboarding": return "Onboarding update";
    case "status_change": return "Status changed";
    default: return "System update";
  }
}

export function eventToActivityItem(
  e: AuditEvent,
  extra?: { meta?: string; amount?: string; href?: string },
): ActivityFeedItem {
  return {
    id: e.id,
    kind: activityKindOf(e),
    title: activityTitleOf(e),
    detail: e.description,
    meta: extra?.meta,
    amount: extra?.amount,
    timestamp: e.timestamp,
    href: extra?.href,
  };
}
